import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Settings, Users } from 'lucide-react';
import config from '../../config';
import { useSelectedMetrics, isMetricSelected, getCosClassName, getCpaClassName, useComponentMetrics, getMetricObjectiveClassName, getMetricLabel, libelleConversions, libelleTauxConversion, infobulleEnteteConversion } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import EnteteAvecBulle from '../EnteteAvecBulle';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../../utils/variationUtils';
import { estErreurAcces } from './accesMeta';
import { getDateRangeFromPeriod, calculatePreviousPeriod as calculateComparisonPeriod } from '../../utils/periodUtils';
const API_URL = config.API_URL;

/**
 * Composant table modulaire des performances par genre Facebook Ads
 * Optimisé avec React.memo et useMemo pour les performances
 */
const GenderTable = React.memo(({
  token,
  selectedAccount,
  period,
  customStartDate,
  customEndDate,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedAccountProp = null,
  selectedCampaignFilter = null,
  selectedPlatformFilter = null,
  labelCampaignIds = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'fbads_gender',
  // Le total dédupliqué du compte : voir `renderTotalCell`.
  totalCouverture = null,
  totalRepetition = null,
  /**
   * La définition de la métrique Conversions sur ce compte, transmise par `App.js`.
   *
   * Ce tableau interroge sa propre route et n'a donc pas cette information : c'est la timeline
   * du dashboard qui la porte, et elle descend en propriété — comme la couverture dédupliquée.
   * `null` signifie « rien de configuré », et la métrique compte alors les achats.
   */
  conversionConfig = null
}) => {
  const [data, setData] = useState([]);
  const [previousData, setPreviousData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unsupportedFilterMessage, setUnsupportedFilterMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Récupérer les métriques sélectionnées
  const selectedMetrics = useSelectedMetrics();

  // Hook pour les métriques du composant
  const componentMetrics = useComponentMetrics('fbads_gender', selectedAccountProp?.id || selectedAccount || 'global');

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Helper pour obtenir le label d'une métrique (avec labels personnalisés)
  const getLabel = useCallback((metricKey) => {
    /*
     * ⚠️ **`conv` et `taux_conv` ne se nomment pas dans une table figée.**
     *
     * Leur intitulé dépend de ce que l'utilisateur a choisi de compter : « Conversions (Ajout
     * au panier) » et non « Conversions ». Un nom saisi à la main dans la modale l'emporte
     * toujours, et c'est `getMetricLabel` qui le détient — d'où l'ordre : on lui demande
     * d'abord, et on ne calcule le nom que s'il n'a rien de personnalisé à dire.
     */
    const personnalise = getMetricLabel(metricKey, moduleId, selectedAccountProp?.id || selectedAccount || 'global');
    if (metricKey === 'conv') return libelleConversions(conversionConfig, null, 'fbads') || personnalise;
    if (metricKey === 'taux_conv') return libelleTauxConversion(conversionConfig, null, 'fbads') || personnalise;
    return personnalise;
  }, [moduleId, selectedAccountProp?.id, selectedAccount, conversionConfig]);

  // Fonctions de formatage locales
  const formatNumberLocal = (num) => new Intl.NumberFormat('fr-FR').format(Math.round(num || 0));
  const formatCurrency = (num) => `€${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)}`;
  const formatPercentage = (num) => `${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)}%`;

  // Configuration des métriques pour le rendu dynamique
  const metricConfig = useMemo(() => ({
    impressions: { sortKey: 'impressions', defaultLabel: 'Impressions' },
    couverture: { sortKey: 'reach', defaultLabel: 'Couverture' },
    repetition: { sortKey: 'frequency', defaultLabel: 'Répétition' },
    ctr: { sortKey: 'ctr', defaultLabel: 'CTR' },
    clics: { sortKey: 'clicks', defaultLabel: 'Clics' },
    cpc: { sortKey: 'cpc', defaultLabel: 'CPC' },
    cout: { sortKey: 'cost', defaultLabel: 'Coût' },
    taux_conv: { sortKey: 'conversionRate', defaultLabel: 'Taux conv.' },
    conv: { sortKey: 'conversions', defaultLabel: 'Conv.' },
    cpa: { sortKey: 'cpa', defaultLabel: 'CPA' },
    valeur_conv: { sortKey: 'conversionsValue', defaultLabel: 'Valeur Conv.' },
    /**
     * Les achats, distincts des conversions.
     *
     * `conv` compte ce que l'utilisateur a configuré ; `transactions` compte les achats, et
     * seulement eux. Sur un compte dont les conversions configurées *sont* les achats, les
     * deux colonnes affichent le même nombre — c'est pour cela que celle-ci est décochée par
     * défaut, et non parce qu'elle serait secondaire.
     */
    transactions: { sortKey: 'transactions', defaultLabel: 'Transactions' },
    cos: { sortKey: 'roas', defaultLabel: 'COS' },
    interactions: { sortKey: 'interactions', defaultLabel: 'Interactions' },
    cout_par_interaction: { sortKey: 'cout_par_interaction', defaultLabel: 'Coût/Interaction' },
    reactions: { sortKey: 'reactions', defaultLabel: 'Réactions' },
    cout_par_reaction: { sortKey: 'cout_par_reaction', defaultLabel: 'Coût/Réaction' },
    commentaires: { sortKey: 'comments', defaultLabel: "Commentaires" },
    enregistrements: { sortKey: 'saves', defaultLabel: "Enregistrements" },
    partages: { sortKey: 'shares', defaultLabel: "Partages" },
    jaime_facebook: { sortKey: 'pageLikes', defaultLabel: "J'aime (Facebook)" },
  }), []);

  // Fonction pour rendre une cellule selon la métrique
  const renderMetricCell = useCallback((metricKey, item, prevItem, cpa, cos, prevCpa, prevCos) => {
    const accountId = selectedAccountProp?.id || selectedAccount || 'global';
    switch (metricKey) {
      case 'impressions':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.impressions || 0)}
            {prevItem && renderVariation(calculateVariation(item.impressions || 0, prevItem.impressions || 0))}
          </td>
        );
      /**
       * Couverture et répétition, par segment.
       *
       * Meta déduplique la couverture **à l'intérieur de chaque segment** : la valeur d'une
       * ligne est donc juste. Leur somme ne l'est pas — une personne vue sur deux segments y
       * figure deux fois —, d'où le tiret sur la ligne de total (voir `renderTotalCell`).
       */
      case 'couverture':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.reach || 0)}
            {prevItem && renderVariation(calculateVariation(item.reach || 0, prevItem.reach || 0))}
          </td>
        );
      case 'repetition':
        return (
          <td key={metricKey} className="numeric">
            {(item.frequency || 0).toFixed(2)}
            {prevItem && renderVariation(calculateVariation(item.frequency || 0, prevItem.frequency || 0))}
          </td>
        );
      case 'ctr':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('ctr', item.ctr, moduleId, accountId)}`}>
            {formatPercentage(item.ctr || 0)}
            {prevItem && renderVariation(calculateVariation(item.ctr || 0, prevItem.ctr || 0))}
          </td>
        );
      case 'clics':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.clicks || 0)}
            {prevItem && renderVariation(calculateVariation(item.clicks || 0, prevItem.clicks || 0))}
          </td>
        );
      case 'cpc':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cpc', item.cpc, moduleId, accountId)}`}>
            {formatCurrency(item.cpc || 0)}
            {prevItem && renderVariation(calculateVariation(item.cpc || 0, prevItem.cpc || 0), true)}
          </td>
        );
      case 'cout':
        return (
          <td key={metricKey} className="numeric">
            {formatCurrency(item.cost || 0)}
            {prevItem && renderVariation(calculateVariation(item.cost || 0, prevItem.cost || 0), true)}
          </td>
        );
      case 'taux_conv':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('taux_conv', item.conversionRate, moduleId, accountId)}`}>
            {formatPercentage(item.conversionRate || 0)}
            {prevItem && renderVariation(calculateVariation(item.conversionRate || 0, prevItem.conversionRate || 0))}
          </td>
        );
      case 'conv':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.conversions || 0)}
            {prevItem && renderVariation(calculateVariation(item.conversions || 0, prevItem.conversions || 0))}
          </td>
        );
      case 'transactions':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.transactions || 0)}
            {prevItem && renderVariation(calculateVariation(item.transactions || 0, prevItem.transactions || 0))}
          </td>
        );
      case 'cpa':
        return (
          <td key={metricKey} className={`numeric ${getCpaClassName(cpa, accountId, moduleId)}`}>
            {cpa >= 0 ? formatCurrency(cpa) : '-'}
            {prevItem && renderVariation(calculateVariation(cpa, prevCpa), true)}
          </td>
        );
      case 'valeur_conv':
        return (
          <td key={metricKey} className="numeric">
            {formatCurrency(item.conversionsValue || 0)}
            {prevItem && renderVariation(calculateVariation(item.conversionsValue || 0, prevItem.conversionsValue || 0))}
          </td>
        );
      case 'cos':
        return (
          <td key={metricKey} className={`numeric ${getCosClassName(cos, accountId, moduleId)}`}>
            {formatNumber ? `${formatNumber(cos, 1)}%` : `${cos.toFixed(1)}%`}
            {prevItem && renderVariation(calculateVariation(cos, prevCos), true)}
          </td>
        );
      case 'interactions':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.interactions || 0)}
            {prevItem && renderVariation(calculateVariation(item.interactions || 0, prevItem.interactions || 0))}
          </td>
        );
      case 'cout_par_interaction': {
        const coutParInt = item.interactions > 0 ? (item.cost / item.interactions) : 0;
        const prevCoutParInt = prevItem && prevItem.interactions > 0 ? (prevItem.cost / prevItem.interactions) : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cout_par_interaction', coutParInt, moduleId, accountId)}`}>
            {coutParInt >= 0 ? formatCurrency(coutParInt) : '-'}
            {prevItem && renderVariation(calculateVariation(coutParInt, prevCoutParInt), true)}
          </td>
        );
      }
      case 'reactions':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.reactions || 0)}
            {prevItem && renderVariation(calculateVariation(item.reactions || 0, prevItem.reactions || 0))}
          </td>
        );
      case 'commentaires':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.comments || 0)}
            {prevItem && renderVariation(calculateVariation(item.comments || 0, prevItem.comments || 0))}
          </td>
        );
      case 'enregistrements':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.saves || 0)}
            {prevItem && renderVariation(calculateVariation(item.saves || 0, prevItem.saves || 0))}
          </td>
        );
      case 'partages':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.shares || 0)}
            {prevItem && renderVariation(calculateVariation(item.shares || 0, prevItem.shares || 0))}
          </td>
        );
      case 'jaime_facebook':
        return (
          <td key={metricKey} className="numeric">
            {formatNumberLocal(item.pageLikes || 0)}
            {prevItem && renderVariation(calculateVariation(item.pageLikes || 0, prevItem.pageLikes || 0))}
          </td>
        );
      case 'cout_par_reaction': {
        const coutParReact = item.reactions > 0 ? (item.cost / item.reactions) : 0;
        const prevCoutParReact = prevItem && prevItem.reactions > 0 ? (prevItem.cost / prevItem.reactions) : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cout_par_reaction', coutParReact, moduleId, accountId)}`}>
            {coutParReact >= 0 ? formatCurrency(coutParReact) : '-'}
            {prevItem && renderVariation(calculateVariation(coutParReact, prevCoutParReact), true)}
          </td>
        );
      }
      case 'roas': {
        const roas = item.cost > 0 ? (item.conversionsValue / item.cost) * 100 : 0;
        const prevRoas = prevItem && prevItem.cost > 0 ? (prevItem.conversionsValue / prevItem.cost) * 100 : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, accountId)}`}>
            {roas >= 0 ? `${formatNumber ? formatNumber(roas, 0) : roas.toFixed(0)}%` : '-'}
            {prevItem && renderVariation(calculateVariation(roas, prevRoas))}
          </td>
        );
      }
      case 'roi': {
        const hasData = item.cost > 0 && item.conversionsValue > 0;
        const roi = hasData ? (item.conversionsValue - item.cost) / item.cost : 0;
        const prevHasData = prevItem && prevItem.cost > 0 && prevItem.conversionsValue > 0;
        const prevRoi = prevHasData ? (prevItem.conversionsValue - prevItem.cost) / prevItem.cost : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, accountId)}`}>
            {(formatNumber ? formatNumber(roi, 2) : roi.toFixed(2))}
            {renderVariation(calculateVariation(roi, prevRoi))}
          </td>
        );
      }
      default:
        return null;
    }
  }, [selectedAccountProp?.id, selectedAccount, moduleId, formatNumber]);

  // Fonction pour rendre une cellule de total selon la métrique
  const renderTotalCell = useCallback((metricKey, totals, previousTotals) => {
    const accountId = selectedAccountProp?.id || selectedAccount || 'global';
    switch (metricKey) {
      case 'impressions':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.impressions)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
          </td>
        );
      /**
       * Pas de total pour les deux métriques de personnes.
       *
       * Additionner les couvertures de segments qui se recouvrent donnerait un nombre supérieur
       * à la réalité, et la répétition est un ratio bâti dessus. Le total dédupliqué du compte
       * existe, mais ailleurs : sur le tableau des campagnes et sur la carte « Couverture » des
       * résultats, qui portent sur l'ensemble du compte.
       */
      case 'couverture':
      case 'repetition': {
        /**
         * Le total vient du compte, jamais de la somme des lignes.
         *
         * Les segments se recouvrent : une personne vue sur deux d'entre eux figure dans les
         * deux lignes. Seul Meta sait déduplier, et il le fait pour le compte entier — c'est
         * cette valeur que le dashboard fait descendre. Elle manque dès qu'un filtre rétrécit
         * le tableau : un tiret vaut mieux qu'un chiffre portant sur un autre périmètre.
         */
        const valeurTotale = metricKey === 'couverture' ? totalCouverture : totalRepetition;
        return (
          <td
            key={metricKey}
            className="numeric"
            title={valeurTotale == null
              ? "Les segments se recouvrent : leur somme n'a pas de sens, et le total du compte ne correspond pas à un tableau filtré."
              : 'Couverture dédupliquée du compte, calculée par Meta sur la période'}
          >
            <strong>
              {valeurTotale == null
                ? '—'
                : (metricKey === 'couverture' ? formatNumberLocal(valeurTotale) : valeurTotale.toFixed(2))}
            </strong>
          </td>
        );
      }
      case 'ctr':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('ctr', totals.ctr, moduleId, accountId)}`}>
            <strong>{formatPercentage(totals.ctr)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
          </td>
        );
      case 'clics':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.clicks)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
          </td>
        );
      case 'cpc':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cpc', totals.cpc, moduleId, accountId)}`}>
            <strong>{formatCurrency(totals.cpc)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
          </td>
        );
      case 'cout':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatCurrency(totals.cost)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
          </td>
        );
      case 'taux_conv':
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('taux_conv', totals.conversionRate, moduleId, accountId)}`}>
            <strong>{formatPercentage(totals.conversionRate)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
          </td>
        );
      case 'conv':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.conversions)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
          </td>
        );
      case 'transactions':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.transactions || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.transactions || 0, previousTotals.transactions || 0))}
          </td>
        );
      case 'cpa':
        return (
          <td key={metricKey} className={`numeric ${getCpaClassName(totals.cpa, accountId, moduleId)}`}>
            <strong>{totals.cpa >= 0 ? formatCurrency(totals.cpa) : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
          </td>
        );
      case 'valeur_conv':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatCurrency(totals.conversionsValue)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
          </td>
        );
      case 'cos':
        return (
          <td key={metricKey} className={`numeric ${getCosClassName(totals.cos, accountId, moduleId)}`}>
            <strong>{formatNumber ? `${formatNumber(totals.cos, 1)}%` : `${totals.cos.toFixed(1)}%`}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
          </td>
        );
      case 'interactions':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.interactions)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.interactions, previousTotals.interactions))}
          </td>
        );
      case 'cout_par_interaction': {
        const coutParInt = totals.interactions > 0 ? (totals.cost / totals.interactions) : 0;
        const prevCoutParInt = previousTotals && previousTotals.interactions > 0 ? (previousTotals.cost / previousTotals.interactions) : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cout_par_interaction', coutParInt, moduleId, accountId)}`}>
            <strong>{coutParInt >= 0 ? formatCurrency(coutParInt) : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(coutParInt, prevCoutParInt), true)}
          </td>
        );
      }
      case 'reactions':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.reactions)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.reactions, previousTotals.reactions))}
          </td>
        );
      case 'commentaires':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.comments || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.comments || 0, previousTotals.comments || 0))}
          </td>
        );
      case 'enregistrements':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.saves || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.saves || 0, previousTotals.saves || 0))}
          </td>
        );
      case 'partages':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.shares || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.shares || 0, previousTotals.shares || 0))}
          </td>
        );
      case 'jaime_facebook':
        return (
          <td key={metricKey} className="numeric">
            <strong>{formatNumberLocal(totals.pageLikes || 0)}</strong>
            {previousTotals && renderVariation(calculateVariation(totals.pageLikes || 0, previousTotals.pageLikes || 0))}
          </td>
        );
      case 'cout_par_reaction': {
        const coutParReact = totals.reactions > 0 ? (totals.cost / totals.reactions) : 0;
        const prevCoutParReact = previousTotals && previousTotals.reactions > 0 ? (previousTotals.cost / previousTotals.reactions) : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('cout_par_reaction', coutParReact, moduleId, accountId)}`}>
            <strong>{coutParReact >= 0 ? formatCurrency(coutParReact) : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(coutParReact, prevCoutParReact), true)}
          </td>
        );
      }
      case 'roas': {
        const roas = totals.cost > 0 ? (totals.conversionsValue / totals.cost) * 100 : 0;
        const prevRoas = previousTotals && previousTotals.cost > 0 ? (previousTotals.conversionsValue / previousTotals.cost) * 100 : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, accountId)}`}>
            <strong>{roas >= 0 ? `${formatNumber ? formatNumber(roas, 0) : roas.toFixed(0)}%` : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </td>
        );
      }
      case 'roi': {
        const hasData = totals.cost > 0 && totals.conversionsValue > 0;
        const roi = hasData ? (totals.conversionsValue - totals.cost) / totals.cost : 0;
        const prevHasData = previousTotals && previousTotals.cost > 0 && previousTotals.conversionsValue > 0;
        const prevRoi = prevHasData ? (previousTotals.conversionsValue - previousTotals.cost) / previousTotals.cost : 0;
        return (
          <td key={metricKey} className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, accountId)}`}>
            <strong>{(formatNumber ? formatNumber(roi, 2) : roi.toFixed(2))}</strong>
            {renderVariation(calculateVariation(roi, prevRoi))}
          </td>
        );
      }
      default:
        return null;
    }
  // `totalCouverture` et `totalRepetition` font partie des dépendances : sans elles, le
  // rappel mémoïsé garderait le total du compte précédent après un changement de compte
  // ou de période.
  }, [selectedAccountProp?.id, selectedAccount, moduleId, formatNumber, totalCouverture, totalRepetition]);





  const fetchGenderData = useCallback(async (currentLabelCampaignIds) => {
    console.log('👥 [GENDER-DEBUG] selectedAccount:', selectedAccount);
    console.log('👥 [GENDER-DEBUG] token:', token ? `Token présent (${token.length} chars)` : 'Token manquant');
    console.log('👥 [GENDER-DEBUG] currentLabelCampaignIds:', currentLabelCampaignIds);

    if (!selectedAccount || !token) {
      console.log('👥 [GENDER-DEBUG] Conditions non remplies - selectedAccount:', !!selectedAccount, 'token:', !!token);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('👥 [GENDER-DEBUG] Appel API POST vers:', `${API_URL}/api/facebook-ads/gender`);
      const requestBody = {
        accountId: selectedAccount,
        period,
        customStartDate,
        customEndDate,
        ...(selectedCampaignFilter && { campaignFilter: selectedCampaignFilter }),
        ...(selectedPlatformFilter && { platformFilter: selectedPlatformFilter }),
        ...(currentLabelCampaignIds && { labelCampaignIds: currentLabelCampaignIds })
      };

      console.log('👥 [GENDER-DEBUG] Body:', requestBody);
      console.log('👥 [GENDER-DEBUG] Headers:', { Authorization: `Bearer ${token.substring(0, 10)}...` });

      const response = await axios.post(
        `${API_URL}/api/facebook-ads/gender`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setData(response.data.data || []);
        setUnsupportedFilterMessage(response.data.unsupportedFilter ? response.data.message : null);

        // Récupérer les données de la période précédente si comparaison activée
        if (compareEnabled && comparisonType) {
          let currentStart = customStartDate;
          let currentEnd = customEndDate;
          if (!currentStart || !currentEnd) {
            const dateRange = getDateRangeFromPeriod(period);
            currentStart = dateRange.startDate;
            currentEnd = dateRange.endDate;
          }
          const comparisonDates = calculateComparisonPeriod(currentStart, currentEnd, comparisonType);
          console.log('👥 [GENDER-COMPARE] Récupération période précédente:', comparisonDates);

          const prevResponse = await axios.post(
            `${API_URL}/api/facebook-ads/gender`,
            { ...requestBody, period: 'CUSTOM', customStartDate: comparisonDates.startDate, customEndDate: comparisonDates.endDate },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (prevResponse.data.success) {
            setPreviousData(prevResponse.data.data || []);
          }
        } else {
          setPreviousData([]);
        }
      } else {
        setError(response.data.error || 'Erreur lors du chargement des données');
        setUnsupportedFilterMessage(null);
      }
    } catch (err) {
      console.error('👥 [GENDER-DEBUG] Erreur complète:', err);
      console.error('👥 [GENDER-DEBUG] Status:', err.response?.status);
      console.error('👥 [GENDER-DEBUG] Data:', err.response?.data);
      console.error('👥 [GENDER-DEBUG] Message:', err.message);
      
      let errorMessage = 'Erreur lors du chargement des données';
      
      if (err.response?.status === 401) {
        errorMessage = 'Erreur d\'authentification - veuillez vous reconnecter';
      } else if (err.response?.status === 404) {
        errorMessage = 'Endpoint non trouvé - vérifiez la configuration du serveur';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, period, customStartDate, customEndDate, token, selectedCampaignFilter, selectedPlatformFilter, compareEnabled, comparisonType]);

  useEffect(() => {
    console.log('👥 [GENDER-EFFECT] labelCampaignIds reçu:', labelCampaignIds);
    console.log('👥 [GENDER-EFFECT] selectedAccount:', selectedAccount);
    if (selectedAccount && token) {
      fetchGenderData(labelCampaignIds);
    }
  }, [selectedAccount, period, customStartDate, customEndDate, token, selectedCampaignFilter, selectedPlatformFilter, labelCampaignIds, fetchGenderData, compareEnabled, comparisonType]);

  // Fonction de tri memoized
  const sortedData = useMemo(() => {
    if (!data.length) return [];

    const sorted = [...data];
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      // Calcul spécial pour CPA
      if (sortConfig.key === 'cpa') {
        aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
        bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!data.length) return {};

    const baseTotals = data.reduce((acc, item) => ({
      impressions: acc.impressions + (item.impressions || 0),
      clicks: acc.clicks + (item.clicks || 0),
      cost: acc.cost + (item.cost || 0),
      conversions: acc.conversions + (item.conversions || 0),
      transactions: acc.transactions + (item.transactions || 0),
      conversionsValue: acc.conversionsValue + (item.conversionsValue || 0),
      interactions: acc.interactions + (item.interactions || 0),
      reactions: acc.reactions + (item.reactions || 0),
      comments: acc.comments + (item.comments || 0),
      saves: acc.saves + (item.saves || 0),
      shares: acc.shares + (item.shares || 0),
      pageLikes: acc.pageLikes + (item.pageLikes || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, transactions: 0, interactions: 0, reactions: 0, comments: 0, saves: 0, shares: 0, pageLikes: 0 });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      cout_par_interaction: baseTotals.interactions > 0 ? (baseTotals.cost / baseTotals.interactions) : 0,
      cout_par_reaction: baseTotals.reactions > 0 ? (baseTotals.cost / baseTotals.reactions) : 0
    };
  }, [data]);

  // Calcul des totaux précédents memoized
  const previousTotals = useMemo(() => {
    if (!previousData.length) return null;

    const baseTotals = previousData.reduce((acc, item) => ({
      impressions: acc.impressions + (item.impressions || 0),
      clicks: acc.clicks + (item.clicks || 0),
      cost: acc.cost + (item.cost || 0),
      conversions: acc.conversions + (item.conversions || 0),
      transactions: acc.transactions + (item.transactions || 0),
      conversionsValue: acc.conversionsValue + (item.conversionsValue || 0),
      interactions: acc.interactions + (item.interactions || 0),
      reactions: acc.reactions + (item.reactions || 0),
      comments: acc.comments + (item.comments || 0),
      saves: acc.saves + (item.saves || 0),
      shares: acc.shares + (item.shares || 0),
      pageLikes: acc.pageLikes + (item.pageLikes || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, transactions: 0, interactions: 0, reactions: 0, comments: 0, saves: 0, shares: 0, pageLikes: 0 });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      cout_par_interaction: baseTotals.interactions > 0 ? (baseTotals.cost / baseTotals.interactions) : 0,
      cout_par_reaction: baseTotals.reactions > 0 ? (baseTotals.cost / baseTotals.reactions) : 0
    };
  }, [previousData]);

  // Configuration de pagination
  const itemsPerPage = 20;
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Réinitialiser la page quand les données changent
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Données paginées memoized
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage, totalItems]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  }, [sortConfig]);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Ne pas rendre si pas de données et pas de loading/erreur
  if (!loading && !error && !data.length) return null;

  // Vérification de visibilité pour les clients
  /**
   * Accès Meta expiré : la section disparaît, le bandeau de tête suffit.
   *
   * Elle affichait sinon le code brut du serveur — « ❌ auth_expired » — sous son titre, alors
   * que l'écran porte déjà, une fois, l'avertissement et le bouton qui le résout. Un bloc vide
   * sous son intitulé se lit comme une panne de la section ; c'est la connexion qui est en
   * cause, et elle est annoncée là où on peut agir.
   */
  if (estErreurAcces(error)) return null;

  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Users size={16} />&nbsp;Genre</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Genre"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && isVisible && (
        <>
          {loading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <span>Chargement des données par genre...</span>
            </div>
          )}

          {error && (
            <div className="error-container">
              <span>❌ {error}</span>
            </div>
          )}

          {!loading && !error && data.length === 0 && (
            <div className="no-data-container">
              <span>{unsupportedFilterMessage || 'Aucune donnée disponible pour cette période'}</span>
            </div>
          )}

          {!loading && !error && data.length > 0 && (
            <>
              <div className="campaigns-table-wrapper">
                <table 
                  key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')} 
                  className="campaigns-table" 
                  style={{ 
                    width: '100%', 
                    tableLayout: 'auto', 
                    borderCollapse: 'collapse'
                  }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('gender')} className="sortable">
                        Genre {sortConfig.key === 'gender' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                      {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                      {componentMetrics.order
                        .filter(key => isComponentMetricActive(key) && metricConfig[key])
                        .map(metricKey => {
                          const config = metricConfig[metricKey];
                          const label = getLabel(metricKey) || config.defaultLabel;
                          return (
                            <EnteteAvecBulle key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric" texte={infobulleEnteteConversion(metricKey, conversionConfig)}>
                              {label} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                            </EnteteAvecBulle>
                          );
                        })}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((item, index) => {
                      const cpa = item.conversions > 0 ? (item.cost / item.conversions) : 0;
                      const cos = item.conversionsValue > 0 ? (item.cost / item.conversionsValue * 100) : 0;

                      // Trouver les données précédentes pour ce genre
                      const prevItem = compareEnabled && previousData.length > 0
                        ? previousData.find(p => p.gender === item.gender)
                        : null;
                      const prevCpa = prevItem && prevItem.conversions > 0 ? (prevItem.cost / prevItem.conversions) : 0;
                      const prevCos = prevItem && prevItem.conversionsValue > 0 ? (prevItem.cost / prevItem.conversionsValue * 100) : 0;

                      return (
                        <tr key={index}>
                          <td className="campaign-name">
                            {/* Casse ramenée en minuscules : Meta n'est pas constant d'un
                                découpage à l'autre — « unknown » ici, « Unknown » sur les âges.
                                Une comparaison stricte laisse passer le mot anglais. */}
                            {(() => {
                              const genre = String(item.gender ?? '').toLowerCase();
                              if (genre === 'male') return '👨 Homme';
                              if (genre === 'female') return '👩 Femme';
                              if (genre === 'unknown') return '❓ Non défini';
                              return item.gender;
                            })()}
                          </td>
                          {/* Rendu dynamique des cellules selon l'ordre configuré */}
                          {componentMetrics.order
                            .filter(key => isComponentMetricActive(key) && metricConfig[key])
                            .map(metricKey => renderMetricCell(metricKey, item, prevItem, cpa, cos, prevCpa, prevCos))}
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr className="totals-row">
                      <td className="campaign-name"><strong>TOTAL</strong></td>
                      {/* Rendu dynamique des cellules de totaux selon l'ordre configuré */}
                      {componentMetrics.order
                        .filter(key => isComponentMetricActive(key) && metricConfig[key])
                        .map(metricKey => renderTotalCell(metricKey, totals, previousTotals))}
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Contrôles de pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    <span>
                      Affichage {startIndex + 1}-{Math.min(endIndex, sortedData.length)} sur {sortedData.length} segments
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      ← Précédent
                    </button>
                    <span className="page-numbers">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="fbads_gender"
          accountId={selectedAccountProp?.id || selectedAccount || 'global'}
          title="Genre Meta Ads"
          hideApplyToAll={true}
          /*
           * La définition de la métrique Conversions se règle ici, dans l'engrenage de la
           * métrique — comme sur le dashboard Google Analytics. Ces quatre propriétés sont
           * ce dont la modale a besoin pour la charger, l'enregistrer et la rendre.
           *
           * `onAdvancedConfigSave` recharge : les séries et tous les tableaux sont calculés
           * côté serveur avec l'ancienne définition. Sans ce rechargement, l'écran afficherait
           * le nouveau nom au-dessus des anciens chiffres — pire qu'un écran inchangé,
           * puisque rien ne le signalerait.
           */
          token={token}
          propertyId={selectedAccountProp?.id || selectedAccount}
          currentAdvancedConfig={conversionConfig}
          onAdvancedConfigSave={() => window.location.reload()}
        />
      )}
    </div>
  );
});

GenderTable.displayName = 'GenderTable';

export default GenderTable;